/* ==========================================================================
   Sunboost custom — sb-product-video web component
   v3 — custom reveal + center cycle button (no native HTML5 controls,
   no download button). 2026-04-14.

   Flow:
   - Initial: poster image visible with amber play-pip overlay.
   - Click poster → clone <template> contents into .sb-pv__player, hide poster.
   - For native <video>: play with sound (poster click is the user gesture),
     wire a single transparent center button that toggles play/pause. Button
     is hover-only on hover-capable devices when the video is playing; always
     visible when paused (CSS handles the visibility — JS just toggles
     .is-playing on the host).
   - For YouTube/Vimeo iframes: no custom button — those embeds bring their
     own controls.
   ========================================================================== */

(function () {
  if (window.customElements && window.customElements.get('sb-product-video')) return;

  var NOOP = function () {};

  class SbProductVideo extends HTMLElement {
    constructor() {
      super();
      this._activated = false;
      this._video = null;
      this._onPosterClick = this._onPosterClick.bind(this);
      this._onPosterKey = this._onPosterKey.bind(this);
      this._onCycleClick = this._onCycleClick.bind(this);
      this._onFullscreenClick = this._onFullscreenClick.bind(this);
      this._onVideoClick = this._onVideoClick.bind(this);
      this._sync = this._sync.bind(this);
    }

    connectedCallback() {
      this.poster   = this.querySelector('.sb-pv__poster');
      this.player   = this.querySelector('.sb-pv__player');
      this.tpl      = this.querySelector('template');
      this.cycleBtn = this.querySelector('.sb-pv__cycle');
      this.fsBtn    = this.querySelector('.sb-pv__fs');

      if (!this.poster || !this.player || !this.tpl) return;

      this.poster.addEventListener('click', this._onPosterClick);
      this.poster.addEventListener('keydown', this._onPosterKey);
    }

    disconnectedCallback() {
      if (this.poster) {
        this.poster.removeEventListener('click', this._onPosterClick);
        this.poster.removeEventListener('keydown', this._onPosterKey);
      }
      if (this.cycleBtn) {
        this.cycleBtn.removeEventListener('click', this._onCycleClick);
      }
      if (this.fsBtn) {
        this.fsBtn.removeEventListener('click', this._onFullscreenClick);
      }
      if (this._video) {
        this._video.removeEventListener('click', this._onVideoClick);
        this._video.removeEventListener('play', this._sync);
        this._video.removeEventListener('pause', this._sync);
        this._video.removeEventListener('ended', this._sync);
      }
    }

    _onPosterClick() { this._activate(); }

    _onPosterKey(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._activate();
      }
    }

    _activate() {
      if (this._activated) return;
      this._activated = true;

      // Inject the template's video/iframe into the player layer.
      this.player.appendChild(this.tpl.content.cloneNode(true));
      this.player.setAttribute('aria-hidden', 'false');

      // Hide the poster.
      this.poster.classList.add('is-hidden');
      this.poster.setAttribute('aria-hidden', 'true');

      var videoEl = this.player.querySelector('video');
      if (!videoEl) {
        // iframe path (YouTube / Vimeo) — those embeds bring their own UI.
        // Nothing more to wire up.
        return;
      }

      // Defensive: kill download / playback-rate / picture-in-picture chrome
      // even if some browser would still show them via right-click etc.
      videoEl.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
      videoEl.setAttribute('disablepictureinpicture', '');
      videoEl.setAttribute('disableremoteplayback', '');
      videoEl.removeAttribute('controls');

      this._video = videoEl;

      // Click anywhere on the video toggles play/pause too.
      videoEl.addEventListener('click', this._onVideoClick);

      // Wire the cycle button.
      if (this.cycleBtn) {
        this.cycleBtn.removeAttribute('hidden');
        this.cycleBtn.addEventListener('click', this._onCycleClick);
      }

      // Wire the fullscreen button.
      if (this.fsBtn) {
        this.fsBtn.removeAttribute('hidden');
        this.fsBtn.addEventListener('click', this._onFullscreenClick);
      }

      videoEl.addEventListener('play', this._sync);
      videoEl.addEventListener('pause', this._sync);
      videoEl.addEventListener('ended', this._sync);

      // The poster click is a valid user gesture, so unmuted play should be
      // allowed. Fall back silently if the browser still blocks it.
      var p = videoEl.play();
      if (p && typeof p.catch === 'function') p.catch(NOOP);

      // Sync once so the cycle button state is correct from the start.
      this._sync();
    }

    _onCycleClick(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!this._video) return;
      if (this._video.paused) {
        var p = this._video.play();
        if (p && typeof p.catch === 'function') p.catch(NOOP);
      } else {
        this._video.pause();
      }
    }

    _onVideoClick() {
      if (!this._video) return;
      if (this._video.paused) {
        var p = this._video.play();
        if (p && typeof p.catch === 'function') p.catch(NOOP);
      } else {
        this._video.pause();
      }
    }

    /* ── Fullscreen — TOGGLE: enter when not in fullscreen, exit when in.
     *    Host element on desktop; iOS uses webkitEnterFullscreen on the
     *    <video> (native iOS player has its own exit affordance). ────── */
    _onFullscreenClick(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!this._video) return;

      // Currently fullscreen → exit.
      var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (fsEl) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(NOOP);
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      // Not in fullscreen → enter.
      if (typeof this._video.webkitEnterFullscreen === 'function' && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
        this._video.webkitEnterFullscreen();
        return;
      }
      var target = this;
      if (target.requestFullscreen) {
        target.requestFullscreen().catch(NOOP);
      } else if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen();
      } else if (this._video.requestFullscreen) {
        this._video.requestFullscreen().catch(NOOP);
      }
    }

    _sync() {
      if (!this._video) return;
      var playing = !this._video.paused && !this._video.ended;
      this.classList.toggle('is-playing', playing);
      if (!this.cycleBtn) return;
      var iconPause = this.cycleBtn.querySelector('.sb-pv-icon--pause');
      var iconPlay  = this.cycleBtn.querySelector('.sb-pv-icon--play');
      if (iconPause) iconPause.classList.toggle('sb-pv-icon--hidden', !playing);
      if (iconPlay)  iconPlay.classList.toggle('sb-pv-icon--hidden',  playing);
      this.cycleBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }
  }

  customElements.define('sb-product-video', SbProductVideo);
})();
